/**
 * Per-visitor popup state: anonymous identity + show-frequency gating.
 *
 * Split out of `popup-conditions.ts` on purpose. Targeting and schedule are
 * pure functions of the page, so they can be unit-tested and reused by the
 * API; frequency needs storage that only the browser has, which would drag
 * a side effect into that shared module.
 *
 * Identity note: `visitorToken` (see `main.ts`) only exists once a visitor
 * opens the chat panel. A popup fires *before* anyone clicks the launcher,
 * so it needs its own id — a UUID in localStorage, minted client-side and
 * never tied to a login.
 */

import type { PopupFrequency } from '@prochat/shared'

export interface PopupFrequencyState {
  count: number
  /** Epoch ms of the last impression; 0 when never shown. */
  lastShownAt: number
}

export interface PopupStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

const ANON_ID_PREFIX = 'prochat.anonId.'
const FREQUENCY_PREFIX = 'prochat.popup.'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export function anonymousIdStorageKey(appKey: string): string {
  return `${ANON_ID_PREFIX}${appKey}`
}

export function frequencyStorageKey(popupId: string): string {
  return `${FREQUENCY_PREFIX}${popupId}`
}

/**
 * v4-ish id. `crypto.randomUUID` is unavailable on http:// origins and in
 * older Safari, so fall back to a timestamp + random suffix rather than
 * failing to identify the visitor at all.
 */
export function newAnonymousId(): string {
  const cryptoRef = globalThis.crypto
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID()
  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Stable id for this browser + app. Throws nothing when storage is blocked
 * (Safari private mode): a fresh id is returned for that page load, which
 * costs the visitor their frequency history and nothing else.
 */
export function resolveAnonymousId(appKey: string, storage?: PopupStorage | null): string {
  const store = storage ?? safeLocalStorage()
  if (!store) return newAnonymousId()
  try {
    const existing = store.getItem(anonymousIdStorageKey(appKey))
    if (existing) return existing
    const created = newAnonymousId()
    store.setItem(anonymousIdStorageKey(appKey), created)
    return created
  } catch {
    return newAnonymousId()
  }
}

function safeLocalStorage(): PopupStorage | null {
  try {
    const ls = window.localStorage
    if (!ls) return null
    return {
      getItem: (key) => ls.getItem(key),
      setItem: (key, value) => ls.setItem(key, value),
    }
  } catch {
    return null
  }
}

export function readFrequencyState(
  popupId: string,
  storage?: PopupStorage | null,
): PopupFrequencyState {
  const store = storage ?? safeLocalStorage()
  if (!store) return { count: 0, lastShownAt: 0 }
  try {
    const raw = store.getItem(frequencyStorageKey(popupId))
    if (!raw) return { count: 0, lastShownAt: 0 }
    const parsed = JSON.parse(raw) as Partial<PopupFrequencyState>
    return {
      count: typeof parsed.count === 'number' && parsed.count > 0 ? parsed.count : 0,
      lastShownAt:
        typeof parsed.lastShownAt === 'number' && parsed.lastShownAt > 0
          ? parsed.lastShownAt
          : 0,
    }
  } catch {
    // Junk in storage must not wedge the popup permanently — start fresh.
    return { count: 0, lastShownAt: 0 }
  }
}

export function writeFrequencyState(
  popupId: string,
  state: PopupFrequencyState,
  storage?: PopupStorage | null,
): void {
  const store = storage ?? safeLocalStorage()
  if (!store) return
  try {
    store.setItem(frequencyStorageKey(popupId), JSON.stringify(state))
  } catch {
    // Quota / private mode — the popup still shows, just again next visit.
  }
}

/**
 * Frequency gate. `seenThisSession` is tracked in memory rather than
 * storage so a reload gives the visitor a genuine second chance — a page
 * refresh is the single most common "I didn't mean to close that" gesture.
 */
export function frequencyAllows(args: {
  frequency: PopupFrequency | undefined
  state: PopupFrequencyState
  seenThisSession: boolean
  now: number
}): { show: boolean; reason: string } {
  const { frequency, state, seenThisSession, now } = args
  switch (frequency) {
    case undefined:
    case 'always':
      return { show: true, reason: 'always' }
    case 'once_per_session':
      return seenThisSession
        ? { show: false, reason: 'once-per-session' }
        : { show: true, reason: 'once-per-session' }
    case 'once_per_person':
      return state.count > 0
        ? { show: false, reason: 'once-per-person' }
        : { show: true, reason: 'once-per-person' }
    case 'hourly': {
      if (state.lastShownAt === 0) return { show: true, reason: 'hourly' }
      return now - state.lastShownAt >= HOUR_MS
        ? { show: true, reason: 'hourly' }
        : { show: false, reason: 'hourly' }
    }
    case 'daily': {
      if (state.lastShownAt === 0) return { show: true, reason: 'daily' }
      return now - state.lastShownAt >= DAY_MS
        ? { show: true, reason: 'daily' }
        : { show: false, reason: 'daily' }
    }
    default:
      return { show: true, reason: 'always' }
  }
}

export function markShown(
  popupId: string,
  previous: PopupFrequencyState,
  now: number,
  storage?: PopupStorage | null,
): PopupFrequencyState {
  const next: PopupFrequencyState = { count: previous.count + 1, lastShownAt: now }
  writeFrequencyState(popupId, next, storage)
  return next
}

/**
 * Popups already shown during this page load.
 *
 * Module-level on purpose: "once per session" means once for the visitor,
 * not once for each tracker instance. A page that initialises the widget
 * twice (double embed tag, a framework re-running the init effect) must not
 * earn itself a second view of the same popup. Reset per page load, not
 * per tracker — that is what makes it a session rather than a lifetime.
 */
const seenThisSession = new Set<string>()

/** Test-only: clear the page-load memo between cases. */
export function resetPopupSessionSeen(): void {
  seenThisSession.clear()
}

/**
 * Stateful wrapper the widget uses at runtime. Constructed once per popup
 * runner; the session memory is shared across every instance.
 */
export class PopupFrequencyTracker {
  private readonly storage: PopupStorage | null

  constructor(storage?: PopupStorage | null) {
    this.storage = storage ?? safeLocalStorage()
  }

  /** True when the popup may be shown now. Pure check — does not record. */
  allows(popupId: string, frequency: PopupFrequency | undefined, now = Date.now()): boolean {
    return frequencyAllows({
      frequency,
      state: readFrequencyState(popupId, this.storage),
      seenThisSession: seenThisSession.has(popupId),
      now,
    }).show
  }

  /** Record an impression so the next call to {@link allows} respects it. */
  record(popupId: string, now = Date.now()): void {
    seenThisSession.add(popupId)
    markShown(popupId, readFrequencyState(popupId, this.storage), now, this.storage)
  }
}
